import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';

import type { LabelValueOption } from '../../shared/self-service-config.types';
import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile } from '../../dvm-layout-builder/dvm-layout-builder.types';
import { toKpiNumberFormat, toNumberScale } from '../../dvm-layout-builder/dvm-builder-number-format.utils';
import { buildKpiWidgetData, kpiDecorationData } from './kpi-widget.utils';

/**
 * KPI card build form: one dataset column + aggregation (or a formula) that
 * becomes a single headline value. The shell owns title / dataset / targets /
 * the Format accordion; this step owns the KPI-specific fields and builds the
 * KPI part of the generate payload.
 */
@Component({
  selector: 'app-kpi-step',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './kpi-step.component.html',
  styles: [':host { display: contents; }'],
})
export class KpiStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['KPI'] as const;

  kpiField = '';
  kpiAggregation = 'SUM';
  kpiFormula = '';

  get kpiAggregations(): LabelValueOption[] {
    return this.host.layout.options?.kpiAggregations?.length
      ? this.host.layout.options.kpiAggregations
      : [
          { label: 'Sum', value: 'SUM' },
          { label: 'Average', value: 'AVG' },
          { label: 'Count', value: 'COUNT' },
          { label: 'Distinct Count', value: 'COUNT_DISTINCT' },
          { label: 'Minimum', value: 'MIN' },
          { label: 'Maximum', value: 'MAX' },
        ];
  }

  /** A formula (or typed references) replaces Column + Aggregation — and needs no dataset. */
  private get usesFormula(): boolean {
    return !!this.kpiFormula.trim() || this.host.targetReferences(this.host.componentId).length > 0;
  }

  onKpiFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.kpiField = mapping.field;
    this.host.invalidatePreview();
  }

  onKpiFormulaFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.kpiFormula = this.host.appendFormulaToken(this.kpiFormula, mapping.field);
    this.host.invalidatePreview();
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (this.usesFormula) return [];
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    const missing: string[] = [];
    if (!this.kpiField) missing.push('Column');
    if (!this.kpiAggregation) missing.push('Aggregation');
    if (missing.length) missing.push('or a formula instead');
    return missing;
  }

  buildPayload(): Record<string, unknown> {
    const kpiFormula = this.kpiFormula.trim();
    const kpiReferences = this.host.referencesForFormula(this.host.componentId, kpiFormula);
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'CHART',
      chartType: this.host.selectedLayoutType, // 'KPI'
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      field: this.kpiField,
      aggregation: this.kpiAggregation,
      ...(kpiFormula ? { formula: kpiFormula } : {}),
      ...(kpiReferences ? { references: kpiReferences } : {}),
      ...this.host.chartTargetsPayload(),
      ...this.host.headerFormatPayload(),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    this.kpiField = String(payload['field'] ?? '');
    this.kpiAggregation = String(payload['aggregation'] ?? 'SUM');
    this.kpiFormula = String(payload['formula'] ?? '');
    this.host.rehydrateOwnFormulaReferences(this.host.componentId, payload['references']);
    this.host.barFormat = { ...(tile.format || {}) };
  }

  reset(): void {
    this.kpiField = '';
    this.kpiAggregation = 'SUM';
    this.kpiFormula = '';
  }

  /** Widget data for a freshly generated KPI tile (the shell adds it to the canvas). */
  buildWidgetData(response: Record<string, unknown> | null, tileId: string, format?: Record<string, unknown>): Record<string, unknown> {
    return buildKpiWidgetData(response, { tileId, title: this.host.title, format, preferredKey: this.kpiField });
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  tileFromResponse(base: DashboardTile, response: Record<string, unknown> | null): DashboardTile {
    return { ...base, colSpan: 3, rowSpan: 3, kpiData: this.buildWidgetData(response, base.id, base.format) };
  }

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.kpiData) return;
    tile.kpiData = {
      ...tile.kpiData,
      title: (format['title'] as string) || tile.title,
      numberFormat: toKpiNumberFormat(format),
      numberScale: toNumberScale(format['numberFormat']),
      decimals: Number(format['decimals'] ?? 1),
      prefix: (format['prefix'] as string) || '',
      suffix: (format['suffix'] as string) || '',
      infoText: (format['infoText'] as string) || '',
      infoIconPosition: (format['infoIconPosition'] as string) || 'corner',
      alignment: (format['contentAlignment'] as string) || 'center',
      cardLayout: (format['cardLayout'] as string) || '',
      titleFontSize: format['titleFontSize'] ?? '',
      valueFontSize: format['valueFontSize'] ?? '',
      ...kpiDecorationData(format),
    };
  }

  applyRecomputeResult(tile: DashboardTile, { result }: TileRecomputeResult): boolean {
    tile.kpiData = buildKpiWidgetData(result, { tileId: tile.id, title: tile.title, format: tile.format, preferredKey: String(tile.payload?.['field'] ?? '') });
    return true;
  }
}
